<template>
  <div>
    <h5 class="text-center mb-4 text-black">{{ translations('dashboard.sales_analysis') }}</h5>
    <div>
      <vue-element-loading :active="isLoading" color="#888" />
      <div class="d-flex">
        <div class="py-1">
          <div
            v-for="(item, i) in data"
            :key="item.year"
            :class="{ 'mb-3': i < data.length - 1 }"
          >
            <div class="text-right pr-2 pb-1 text-black">{{ `${item.year} ${translations('dashboard.annual_sales')}` }}</div>
            <div class="text-right pr-2 pb-1 text-black">{{ `${item.year} ${translations('dashboard.annual_gross_profit')}` }}</div>
          </div>
        </div>
        <div class="flex-grow-1 graph">
          <div class="bg-grid">
            <div v-for="i in 5" :key="i" class="vertical-line" />
          </div>
          <div
            v-for="(item, i) in data"
            :key="item.year"
            :class="{ 'mb-3': i < data.length - 1 }"
          >
            <div class="d-flex">
              <template v-if="item.data.length">
                <RevenueAndProfitStackedChartBar
                  v-for="(unit, index) in item.data"
                  :key="unit.id"
                  :percentage="unit.revenue"
                  :color="unit.color_code || getColor(index)"
                  :name="unit.name"
                />
              </template>
              <RevenueAndProfitStackedChartBar
                v-else
                percentage="100"
                empty
                name="No Data"
                color="#e6e6e6"
              />
            </div>
            <div class="d-flex">
              <template v-if="item.data.length">
                <RevenueAndProfitStackedChartBar
                  v-for="(unit, index) in item.data"
                  :key="unit.id"
                  :percentage="unit.gross_profit"
                  :color="unit.color_code || getColor(index)"
                  :name="unit.name"
                />
              </template>
              <RevenueAndProfitStackedChartBar
                v-else
                percentage="100"
                empty
                name="No Data"
                color="#e6e6e6"
              />
            </div>
          </div>
        </div>
      </div>
      <RevenueAndProfitStackedNotes v-if="filterParams.unit_2.key === 'category'" :color-categories="colorCategories"/>
    </div>
  </div>
</template>

<script>
import VueElementLoading from 'vue-element-loading'
import RevenueAndProfitStackedChartBar from './RevenueAndProfitStackedChartBar'
import RevenueAndProfitStackedNotes from './RevenueAndProfitStackedNotes'
import _isEmpty from 'lodash/isEmpty'
import { mapState } from 'vuex'

const COLORS = [
  '#FF5C5C',
  '#DA8D53',
  '#E5E16A',
  '#31B075',
  '#1FB1D6',
  '#6A3F8D',
  '#CF2FDA',
  '#FF7070',
  '#E2A678',
  '#EDEA97',
  '#40C989',
  '#4CC7E6',
  '#9364B9',
  '#DB63E3',
  '#FF9999',
  '#EABC9A',
  '#F3F1BA',
  '#70D7A7',
  '#82D8ED',
  '#B18ECC',
  '#E797ED',
  '#FFC2C2',
  '#F1D3BB',
  '#F9F8DC',
  '#90E0BA',
  '#A6E3F2',
  '#CFB9DF',
  '#EFBAF3'
]

const initialData = () => {
  const currentYear = new Date().getFullYear()
  return [
    { year: currentYear, data: [] },
    { year: currentYear - 1, data: [] },
    { year: currentYear - 2, data: [] }
  ]
}

export default {
  name: 'DashboardRevenue',

  components: {
    VueElementLoading,
    RevenueAndProfitStackedChartBar,
    RevenueAndProfitStackedNotes
  },

  props: {
    filterParams: {
      type: Object,
      default: () => ({})
    }
  },

  data () {
    return {
      isLoading: true,
      data: initialData(),
      colorCategories: []
    }
  },

  watch: {
    filterParams: {
      handler: 'fetchData',
      deep: true,
      immediate: true
    }
  },

  computed: {
    ...mapState(['currentLanguage'])
  },

  methods: {
    translations (key) {
      const language = require(`@/locales/${this.currentLanguage}.json`)
      const keys = key.split('.')
      let value = language

      for (const k of keys) {
        value = value[k]
        if (!value) break
      }

      return value || key
    },
    getColor (index = -1) {
      return COLORS[index % COLORS.length]
    },

    async fetchData () {
      try {
        this.isLoading = true
        if (this.filterParams.search) {
          const {
            data: { status, message, data }
          } = await this.$http.post(
            this.$api_url.progress_bar_chart,
            this.filterParams
          )

          if (status) {
            this.data = data
            this.colorCategories = this.$util.getUniqueListBy(data[0].data, 'name')
          } else {
            throw new Error(message)
          }
        }
      } catch (error) {
        this.data = initialData()
        console.log('DashboardRevenue:fetchData:catch(error): ', error)
        if (!_isEmpty(this.filterParams)) {
          this.$toastr.error('エラーのため失敗しました。ご確認後、再試行してください。', 'エラー')
        }
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style lang="css" scoped>
.graph {
  position: relative;
  padding: 4px 1px;
  min-height: 190px;
}

.bg-grid {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: space-between;
}

.vertical-line {
  width: 1px;
  height: 100%;
  background-color: #e6e6e6;
}

::v-deep .velmld-overlay {
  background-color: rgba(255, 255, 255, .6);
}
</style>
